import React, { Fragment } from 'react';
import { ThemeProvider } from 'styled-components';
import Sticky from 'react-stickynode';
import styled from 'styled-components';
import { DrawerProvider } from 'common/src/contexts/DrawerContext';
import { saasModernTheme } from 'common/src/theme/saasModern';
import { ResetCSS } from 'common/src/assets/css/style';
import { GlobalStyle, ContentWrapper } from '../containers/sassModern.style';

import BannerSectionSmall from '../containers/BannerSmall';
import Navbar from '../containers/Navbar';
import Footer from '../containers/Footer';
import SEO from '../components/seo';
import Box from '../../../common/src/components/Box';
import Container from '../../../common/src/components/UI/Container';
import { useTranslation } from 'react-i18next';

const TermsAndConditionContent = styled.div`
  font-family: 'Roboto', sans-serif;
  line-height: 2;
  color: #343d48cc;

  a {
    color: #343d48cc;
    text-decoration: underline;
  }

  ul {
    margin-left: 40px;
  }

  li {
    list-style-type: disc;
  }
`;

export default ({ pageContext }) => {
  const { t } = useTranslation();
  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <SEO
          title={t('Terms and conditions')}
          lang={pageContext.lang}
          meta={[
            {
              name: `robots`,
              content: 'noindex,nofollow',
            },
          ]}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar pageContext={pageContext} />
            </DrawerProvider>
          </Sticky>
          <BannerSectionSmall
            titleText={t('Terms of Service for psono.pw')}
          />
          <Box as="section" id="legal_notice_section">
            <Container>
              <TermsAndConditionContent>
                <p>
                  <b>1. Preamble & Scope</b>
                </p>
                <p>
                  These Terms of Service ("Terms") are entered into by esaqa GmbH, Tiergartenstr. 13, 91247 Vorra, Germany ("esaqa", "we", "us") and the person agreeing to these terms ("User", "you").
                </p>
                <p>
                  These Terms exclusively govern the use of the free online service accessible via the domain psono.pw ("Service"). The purpose of the Service is to provide a free, public instance for demonstrating the capabilities of the Psono software.
                </p>
                <p>
                  These Terms do not apply to any other products or services offered by esaqa, including but not limited to "Psono SaaS" or "Psono Enterprise Edition" licenses. Separate agreements govern those commercial offerings. By using the Service, you agree to these Terms.
                </p>
                <br />
                <p>
                  <b>2. The Service</b>
                </p>
                <p>
                  2.1 We may modify the Service from time to time, for example by adding or removing features, to improve the user experience.
                </p>
                <p>
                  2.2 To use the Service, you must create an account. You are solely responsible for the information you provide, the security of your authentication credentials (e.g., password), and all activities that occur under your account.
                </p>
                <p>
                  2.3 We may modify these Terms from time to time (e.g., to reflect changes in the law or the Service). We will provide you with reasonable notice of any material changes.
                </p>
                <br />
                <p>
                  <b>3. Termination</b>
                </p>
                <p>
                  3.1 You may stop using the Service and terminate these Terms at any time by deleting your account.
                </p>
                <p>
                  3.2 We may terminate your access to the Service for our convenience at any time by providing you with 30 days' prior notice.
                </p>
                <p>
                  3.3 We may suspend or terminate your access to the Service immediately and without prior notice if:
                </p>
                <ul>
                  <li>(a) you are in material breach of these Terms, particularly the User Obligations (Section 4);</li>
                  <li>(b) your account has been inactive for more than 180 days;</li>
                  <li>(c) we are required to do so by law.</li>
                </ul>
                <br />
                <p>
                  <b>4. User Obligations</b>
                </p>
                <p>
                  4.1 You will ensure that your use of the Service complies with these Terms and the Acceptable Use Policy (AUP) available at <a href="https://psono.com/acceptable-use-policy/">https://psono.com/acceptable-use-policy/</a>.
                </p>
                <p>
                  4.2 You will use commercially reasonable efforts to prevent unauthorized access to the Service through your account and notify us promptly of any such unauthorized access or use.
                </p>
                <p>
                  4.3 You will not, and will not allow third parties to: (a) copy, modify, or create a derivative work of the Service; (b) reverse engineer, decompile, or otherwise attempt to extract the source code of the Service; (c) sell, resell, or sublicense the Service; or (d) access or use the Service in violation of the AUP, including for High-Risk Activities or to engage in cryptocurrency mining.
                </p>
                <br />
                <p>
                  <b>5. Intellectual Property</b>
                </p>
                <p>
                  Except as expressly stated, these Terms do not grant either party any rights, implied or otherwise, to the other's content or intellectual property. We own all Intellectual Property Rights in the Service and the underlying software.
                </p>
                <br />
                <p>
                  <b>6. Technical Support</b>
                </p>
                <p>
                  The Service is provided without any dedicated technical support. Any support we may offer is at our sole discretion and does not come with any guarantee or warranty.
                </p>
                <br />
                <p>
                  <b>7. Limitation of Liability</b>
                </p>
                <p>
                  The Service is provided "as is" and "as available," without any express or implied warranties. To the maximum extent permitted by law, esaqa and its affiliates will not be liable for any indirect, special, incidental, or consequential damages of any kind, or for any loss of data, revenue, or profits arising out of or in connection with the Service or these Terms.
                </p>
                <p>
                  Our liability is limited to cases of willful misconduct and gross negligence. This limitation of liability will be given full effect even if any remedy specified in these Terms is deemed to have failed of its essential purpose.
                </p>
                <br />
                <p>
                  <b>8. General Provisions</b>
                </p>
                <p>
                  8.1 Jurisdiction: The place of jurisdiction is Nuremberg, Germany.
                </p>
                <p>
                  8.2 Severability: If any term of these Terms is found to be ineffective, the validity of the other provisions is not affected. The invalid provision shall be replaced by a provision that comes as close as possible to the economic purpose of the original.
                </p>
                <p>
                  8.3 No Agency: These Terms do not create an agency, partnership, or joint venture.
                </p>
                <p>
                  8.4 Export Compliance: You must comply with all applicable German and international export laws and regulations.
                </p>
                <p>
                  8.5 Entire Agreement: These Terms, including the AUP, constitute the entire agreement between you and esaqa concerning the Service.
                </p>
              </TermsAndConditionContent>
            </Container>
          </Box>

          <Footer pageContext={pageContext} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
};

